// Plan's additions to the Project menu, and the sheets they open (rooms/menus.ts; README.md ▸ Menus): the status
// workflow (Next/Previous Status, as the Mac's Structure menu), collections, New from Template and Save Selection as
// Template, alternates, a journal's New Post and post numbering, and the story pages' Name Generator… and Suggest
// Story Times… (the Timeline's wand).
import { projectPath } from '../../shell/route.ts';
import type { OpenedProject } from '@writing-suite/core';
import { validFirstPostNumber } from '@writing-suite/core/plan';
import { firstPostNumberOf } from '@writing-suite/core';
import { createElement } from 'react';
import { announce } from '../../shell/context.ts';
import { planEdit } from '../../store/plan.ts';
import { rememberNewPostCaret } from '../../store/newPostCaret.ts';
import { TextDialog } from '../../views/Margin.tsx';
import type { MenuEntry } from '../../views/Menu.tsx';
import { AlternatesSheet } from '../../views/plan/Alternates.tsx';
import { CollectionEditor } from '../../views/plan/CollectionEditor.tsx';
import { FieldsDialog } from '../../views/FieldsDialog.tsx';
import { templateEntries } from '../../views/plan/templates.ts';
import { NameGeneratorSheet, StoryTimesSheet } from '../../views/story/sheets.tsx';
import type { MenuContext, RoomMenu } from '../menus.ts';

const isMac = () => typeof navigator !== 'undefined' && /Mac/.test(navigator.platform ?? '');

function step(ctx: MenuContext, forward: boolean) {
  const id = ctx.node?.fragmentId;
  if (!ctx.live || !id) return announce('Choose a document first.');
  const moved = planEdit(ctx.live, (e) => e.stepStatus(id, forward));
  if (!moved) return announce(forward ? 'That’s the last status.' : 'That’s the first status.');
  const name = ctx.project.fields().find((f) => f.id === 'status')?.options.find((o) => o.id === moved.status)?.name ?? moved.status;
  announce(`Status: ${name}`);
}

function items(ctx: MenuContext): MenuEntry[] {
  const journal = ctx.project.type === 'journal';
  const hasStatus = !!ctx.project.fields().find((f) => f.id === 'status')?.options.length;
  const out: MenuEntry[] = [];
  if (journal) {
    out.push(
      {
        label: 'New Post',
        disabled: !ctx.canWrite || !ctx.live,
        onSelect: () => {
          const made = planEdit(ctx.live!, (e) => e.newPost({ title: 'Untitled' }));
          rememberNewPostCaret(ctx.projectId, made, true);
          const manuscript = ctx.project.manuscriptId();
          announce('New post, as a draft');
          ctx.navigate(ctx.arrangement.id === manuscript ? ctx.hrefFor('write', made.nodeId) : `${projectPath(ctx.projectId)}/write/${encodeURIComponent(made.nodeId)}`);
        },
      },
      { label: 'Post Numbering…', disabled: !ctx.canWrite, onSelect: () => ctx.openRoomSheet('plan/numbering') },
      { kind: 'separator' },
    );
  }
  if (hasStatus) {
    out.push(
      { label: 'Next Status', shortcut: isMac() ? '⌃⌘]' : 'Ctrl+Alt+]', disabled: !ctx.canWrite || !ctx.node?.fragmentId, onSelect: () => step(ctx, true) },
      { label: 'Previous Status', shortcut: isMac() ? '⌃⌘[' : 'Ctrl+Alt+[', disabled: !ctx.canWrite || !ctx.node?.fragmentId, onSelect: () => step(ctx, false) },
    );
  }
  out.push(
    { label: 'New Collection…', disabled: !ctx.canWrite, onSelect: () => ctx.openRoomSheet('plan/collection') },
    { label: 'Fields…', disabled: !ctx.live, onSelect: () => ctx.openRoomSheet('plan/fields') },
    { label: 'Alternates…', disabled: !ctx.node || !ctx.canWrite, onSelect: () => ctx.openRoomSheet('plan/alternates') },
    { kind: 'separator' },
    { label: 'Name Generator…', disabled: !ctx.canWrite, onSelect: () => ctx.openRoomSheet('plan/names') },
    { label: 'Suggest Story Times…', disabled: !ctx.canWrite, onSelect: () => ctx.openRoomSheet('plan/story-times') },
    { kind: 'separator' },
    ...templateEntries({
      project: ctx.project, live: ctx.live, arrangement: ctx.arrangement, node: ctx.node, canStructure: ctx.canStructure,
      opened: (nodeId) => ctx.navigate(ctx.hrefFor('write', nodeId)),
    }),
  );
  return out;
}

/** New Collection… from the Project menu (the contents' menu has it too). */
function CollectionSheet(ctx: MenuContext & { onClose(): void }) {
  const project = ctx.project as OpenedProject;
  if (!project.record) return null;
  return createElement(CollectionEditor, {
    project,
    search: null,
    onCancel: ctx.onClose,
    onSave: (s) => {
      ctx.onClose();
      if (!ctx.live) return;
      planEdit(ctx.live, (e) => e.putSavedSearch(s));
      announce(`Collection “${s.name}” saved`);
      ctx.navigate(`${projectPath(ctx.projectId)}/plan/corkboard?in=${encodeURIComponent(s.id)}`);
    },
  });
}

/** Fields…: the project's fields and a list's options (the Mac's Project ▸ Fields…). */
function FieldsSheet(ctx: MenuContext & { onClose(): void }) {
  if (!ctx.live) return null;
  return createElement(FieldsDialog, { project: ctx.project, live: ctx.live, write: ctx.canWrite, onClose: ctx.onClose });
}

/** Post ▸ Numbering: the number the next post takes when the journal carries on a blog begun elsewhere. */
function NumberingSheet(ctx: MenuContext & { onClose(): void }) {
  const project = ctx.project as OpenedProject;
  const first = project.record ? firstPostNumberOf(project.record.settings) : null;
  return createElement(TextDialog, {
    title: 'Post Numbering',
    label: 'The next post’s number',
    initial: String(first ?? 1),
    confirm: 'Set',
    message: createElement('p', null, 'For a blog you began elsewhere: the number your next post should take, such as 141. Posts already numbered keep their numbers.'),
    onCancel: ctx.onClose,
    onDone: (text: string) => {
      ctx.onClose();
      const n = Number(text.replace(/[^0-9]/g, ''));
      if (!ctx.live || !Number.isFinite(n)) return;
      try {
        validFirstPostNumber(n);
        planEdit(ctx.live, (e) => e.setFirstPostNumber(n));
        announce(`Posts are numbered from ${n}`);
      } catch (err) {
        announce(err instanceof Error ? err.message : String(err));
      }
    },
  });
}

export const menu: RoomMenu = {
  items,
  sheets: {
    'plan/collection': CollectionSheet, 'plan/alternates': AlternatesSheet, 'plan/numbering': NumberingSheet, 'plan/fields': FieldsSheet,
    'plan/names': NameGeneratorSheet, 'plan/story-times': StoryTimesSheet,
  },
};

